/**
 * @author      nick.coffey@zumper.com (Nick Coffey)
 * @copyright   Copyright (c) 2020, Zumper
 * @description ZumperFooter component
 */

import { LANDING_SCREEN_TENANTS } from '@zcode/manage-routes'
import { IS_ZUMPER_RENTALS } from '@zcode/nr-utils'
import { routesConstants } from '@zcode/zumper-routes'
import classNames from 'classnames'
import React from 'react'
import { Link } from 'react-router-dom'
import { App } from '../../App'
import fairHousingIcon from '../../img/icons/fair_housing_icon.svg'
import fbIcon from '../../img/icons/fb_icon.svg'
import instaIcon from '../../img/icons/insta_icon.svg'
import pinIcon from '../../img/icons/pin_icon.svg'
import twitterIcon from '../../img/icons/twitter_icon.svg'
import zumperLogo from '../../img/logo/zumper_logo.svg'
import { AppStoreLink } from '../AppStoreLink'
import { BBB } from '../BBB'
import { type LinkOrButtonProps, LinkToApp } from '../LinkToApp'
import { PlayStoreLink } from '../PlayStoreLink'
import footerStyles from './Footer.module.scss'

const JustLink = ({ href, to, ...props }: LinkOrButtonProps) =>
  to ? <Link to={to} {...props} /> : <a href={href} {...props} />

export interface FooterProps {
  app?: App
  isFiltersView?: boolean
  className?: string
  containerClass?: string
  LinkOrButton?: React.FC<LinkOrButtonProps>
  now?: number
  onAboutClick?: React.MouseEventHandler
  onAdvertiseClick?: React.MouseEventHandler
  onAndroidClick?: React.MouseEventHandler
  onBlogClick?: React.MouseEventHandler
  onContactClick?: React.MouseEventHandler
  onFacebookClick?: React.MouseEventHandler
  onFairHousingClick?: React.MouseEventHandler
  onFeedbackClick?: React.MouseEventHandler
  onGuidesClick?: React.MouseEventHandler
  onHelpCenterClick?: React.MouseEventHandler
  onInstagramClick?: React.MouseEventHandler
  onIOSClick?: React.MouseEventHandler
  onCareersClick?: React.MouseEventHandler
  onLandlordResourcesClick?: React.MouseEventHandler
  onListPropertyClick?: React.MouseEventHandler
  onPinterestClick?: React.MouseEventHandler
  onPressClick?: React.MouseEventHandler
  onPrivacyClick?: React.MouseEventHandler
  onRecruitClick?: React.MouseEventHandler
  onSitemapClick?: React.MouseEventHandler
  onTenantScreeningClick?: React.MouseEventHandler
  onTOSClick?: React.MouseEventHandler
  onTwitterClick?: React.MouseEventHandler
}

export const Footer = ({
  app,
  className,
  containerClass,
  isFiltersView,
  LinkOrButton = JustLink,
  now,
  onAboutClick,
  onAdvertiseClick,
  onAndroidClick,
  onBlogClick,
  onContactClick,
  onFacebookClick,
  onFairHousingClick,
  onFeedbackClick,
  onGuidesClick,
  onHelpCenterClick,
  onInstagramClick,
  onIOSClick,
  onCareersClick,
  onLandlordResourcesClick,
  onListPropertyClick,
  onPinterestClick,
  onPressClick,
  onPrivacyClick,
  onRecruitClick,
  onSitemapClick,
  onTenantScreeningClick,
  onTOSClick,
  onTwitterClick,
}: FooterProps) => {
  const curYear = (now ? new Date(now) : new Date()).getFullYear()

  return (
    <footer
      aria-hidden={isFiltersView}
      className={classNames(
        footerStyles.container,
        containerClass,
        isFiltersView && footerStyles.isFiltersView
      )}
    >
      <section
        className={classNames(footerStyles.footerRow, footerStyles.footerMain, className)}
        data-nosnippet
      >
        <div className={classNames(footerStyles.column, footerStyles.left)}>
          <h3 className={classNames(footerStyles.bigHeader, footerStyles.logo)}>
            <img alt="zumper" height={20} loading="lazy" src={zumperLogo} width={87} />
          </h3>
          <div className={footerStyles.description}>
            Find your fresh start
            <span className={footerStyles.trademark}>™</span>.
          </div>
          <div className={footerStyles.copyright}>&copy; {curYear} Zumper Inc.</div>
          <div className={footerStyles.followUs}>
            <a
              href="https://facebook.com/zumperinc"
              onClick={onFacebookClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              <img alt="Zumper on Facebook" height={20} loading="lazy" src={fbIcon} width={20} />
            </a>
            <a
              href="https://twitter.com/zumper"
              onClick={onTwitterClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                alt="Zumper on Twitter"
                height={17}
                loading="lazy"
                src={twitterIcon}
                width={20}
              />
            </a>
            <a
              href="https://instagram.com/zumper"
              onClick={onInstagramClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                alt="Zumper on Instagram"
                height={20}
                loading="lazy"
                src={instaIcon}
                width={20}
              />
            </a>
            <a
              href="https://pinterest.com/zumper/"
              onClick={onPinterestClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              <img alt="Zumper on Pinterest" height={21} loading="lazy" src={pinIcon} width={20} />
            </a>
          </div>
          <BBB />
        </div>
        <div className={classNames(footerStyles.column, footerStyles.middle, footerStyles.noWrap)}>
          <h4 className={footerStyles.header}>Company</h4>
          <LinkToApp
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            fromApp={app}
            LinkOrButton={LinkOrButton}
            onClick={onAboutClick}
            rel="noopener noreferrer"
            to="/about"
            toApp={App.NZR}
          >
            About Us
          </LinkToApp>
          <LinkOrButton
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="/careers"
            onClick={onCareersClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Careers
          </LinkOrButton>
          <LinkOrButton
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="/blog/"
            onClick={onBlogClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Blog
          </LinkOrButton>
          <LinkOrButton
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="/blog/category/rental-guides/"
            onClick={onGuidesClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Rental Guides
          </LinkOrButton>
          <LinkToApp
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            fromApp={app}
            LinkOrButton={LinkOrButton}
            onClick={onPressClick}
            rel="noopener noreferrer"
            to="/newsroom"
            toApp={App.NZR}
          >
            Newsroom
          </LinkToApp>
        </div>
        <div className={classNames(footerStyles.column, footerStyles.middle, footerStyles.noWrap)}>
          <h4 className={footerStyles.header}>Support</h4>
          <a
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="https://help.zumper.com/hc/en-us"
            onClick={onHelpCenterClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Help Center
          </a>
          <a
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="https://help.zumper.com/hc/en-us/requests/new"
            onClick={onContactClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Contact Us
          </a>
          <a
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="/terms-and-conditions"
            onClick={onTOSClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms and Conditions
          </a>
          {!IS_ZUMPER_RENTALS && (
            <a
              className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
              href="/sitemap"
              onClick={onSitemapClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              Sitemap
            </a>
          )}
          <a
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="mailto:feedback@zumper.com"
            onClick={onFeedbackClick}
            rel="noopener noreferrer"
          >
            Feedback
          </a>
          <a
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="/fair-housing-rights"
            onClick={onFairHousingClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Fair Housing Rights
          </a>
        </div>

        <div
          className={classNames(
            footerStyles.column,
            footerStyles.middle,
            footerStyles.nowrap,
            footerStyles.privacy
          )}
        >
          <h4 className={footerStyles.header}>Privacy Policy</h4>
          <a
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="/privacy-policy"
            onClick={onPrivacyClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </a>
          <a
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="/privacy-policy#ccpa-notice-of-collection"
            onClick={onPrivacyClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Notice of Collection
          </a>
          <a
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href="/privacy-policy#ccpa-do-not-sell"
            onClick={onPrivacyClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            Do Not Sell or Share My Personal Information
          </a>
        </div>
        <div className={classNames(footerStyles.column, footerStyles.right)}>
          <h4 className={classNames(footerStyles.header, footerStyles.firstHeader)}>Explore</h4>
          <LinkOrButton
            className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
            href={routesConstants.ADVERTISE_URL}
            onClick={onAdvertiseClick}
            rel="noopener noreferrer"
          >
            Advertise with Us
          </LinkOrButton>
          <React.Fragment>
            <LinkToApp
              className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
              fromApp={app}
              LinkOrButton={LinkOrButton}
              onClick={onTenantScreeningClick}
              rel="noopener noreferrer"
              to={`https://www.zumper.com${LANDING_SCREEN_TENANTS}`}
              toApp={App.MANAGE}
            >
              Tenant Screening
            </LinkToApp>
            <LinkOrButton
              className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
              href={routesConstants.ADVERTISE_URL}
              onClick={onListPropertyClick}
              rel="noopener noreferrer"
            >
              List Property
            </LinkOrButton>
            <LinkOrButton
              className="block text-gray-0 text-md font-medium leading-[2.1em] desktop:leading-[1.5em] hover:underline"
              href="/manage/resources/"
              onClick={onLandlordResourcesClick}
              rel="noopener noreferrer"
            >
              Landlord Resources
            </LinkOrButton>
          </React.Fragment>
          <div>
            <div className={classNames(footerStyles.header, footerStyles.secondHeader)}>
              Rental Apps
            </div>
          </div>
          <div className={footerStyles.badges}>
            <AppStoreLink className={footerStyles.apple} onClick={onIOSClick} origin="footer" />
            <PlayStoreLink onClick={onAndroidClick} origin="footer" />
          </div>
        </div>
      </section>
      <div
        className={classNames(footerStyles.footerRow, footerStyles.bottom, className)}
        data-nosnippet
      >
        <div className={footerStyles.fairHousing}>
          <img
            alt=""
            className={footerStyles.house}
            height="16"
            loading="lazy"
            src={fairHousingIcon}
            width="21"
          />
          Fair Housing & Equal Opportunity
        </div>
        <div className={footerStyles.builtBy}>
          <div className={footerStyles.builtByText}>
            Zumper is powered by passionate, innovative employees from every corner of the
            country.&nbsp;
            <LinkOrButton
              className="text-[#dfe2e4] underline hover:text-gray-0 "
              href="/careers"
              onClick={onRecruitClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              Interested in joining the team?
            </LinkOrButton>
          </div>
        </div>
      </div>
    </footer>
  )
}
